/**
 * Teacher load buckets for dashboard widgets (Admin / Staff).
 * Load = total weekly class sessions (sum of classDays per assigned class).
 */

import type {
  GetClassResponse,
  GetTeacherResponse,
} from "@/openapi/generated";
import { DEFAULT_STUDIO_OPERATIONS_SETTINGS } from "@/lib/utils/operations-settings/operations-settings-defaults";
import {
  buildTeacherLoadBucketRanges,
  formatTeacherLoadBucketRangeLabel,
  formatTeacherLoadBucketRangeTitle,
  getTeacherLoadOverworkedThreshold,
} from "@/lib/utils/operations-settings/teacher-load-thresholds";

export interface TeacherLoadRow {
  teacherId: number;
  name: string;
  weeklySessions: number;
  assignedClassCount: number;
  classNames: string[];
  isOverworked: boolean;
}

export interface TeacherLoadBucketRange {
  id: string;
  min: number;
  max: number | null;
}

export interface TeacherLoadThresholds {
  teacherLoadLightMax: number;
  teacherLoadPartTimeMax: number;
  teacherLoadFullTimeMax: number;
}

/** Studio-oriented weekly session buckets (dance, martial arts, yoga, etc.). */
export const TEACHER_LOAD_BUCKET_RANGES: TeacherLoadBucketRange[] =
  buildTeacherLoadBucketRanges(DEFAULT_STUDIO_OPERATIONS_SETTINGS);

/** Sessions/week at or above this count are flagged as high load (heavy bucket). */
export const TEACHER_LOAD_OVERWORKED_THRESHOLD = getTeacherLoadOverworkedThreshold(
  DEFAULT_STUDIO_OPERATIONS_SETTINGS,
);

export const TEACHER_LOAD_BUCKET_COLORS = [
  "#4FD1C5",
  "#68D391",
  "#F6AD55",
  "#E53E3E",
] as const;

export interface TeacherLoadBucket {
  range: TeacherLoadBucketRange;
  teachers: TeacherLoadRow[];
}

function sortTeacherLoadRows(rows: TeacherLoadRow[]): TeacherLoadRow[] {
  return [...rows].sort(
    (a, b) =>
      b.weeklySessions - a.weeklySessions ||
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
}

function weeklySessionsForClass(classItem: GetClassResponse): number {
  return classItem.classDays?.length ?? 0;
}

function teacherDisplayName(
  teacher: Pick<GetTeacherResponse, "firstName" | "lastName" | "teacherId">,
): string {
  const name = `${teacher.firstName ?? ""} ${teacher.lastName ?? ""}`.trim();
  return name || `Teacher ${teacher.teacherId}`;
}

export function computeTeacherLoadFromClassesAndTeachers(
  classes: readonly GetClassResponse[],
  teachers: readonly GetTeacherResponse[],
  thresholds: TeacherLoadThresholds = DEFAULT_STUDIO_OPERATIONS_SETTINGS,
): TeacherLoadRow[] {
  const overworkedThreshold = getTeacherLoadOverworkedThreshold(thresholds);
  const loadByTeacherId = new Map<number, TeacherLoadRow>();

  for (const teacher of teachers) {
    const teacherId = Number(teacher.teacherId);
    if (!Number.isFinite(teacherId)) continue;

    loadByTeacherId.set(teacherId, {
      teacherId,
      name: teacherDisplayName(teacher),
      weeklySessions: 0,
      assignedClassCount: 0,
      classNames: [],
      isOverworked: false,
    });
  }

  for (const classItem of classes) {
    if (classItem.isActive === false || !classItem.teachers?.length) continue;

    const sessions = weeklySessionsForClass(classItem);
    const className =
      typeof classItem.name === "string" && classItem.name.trim().length > 0
        ? classItem.name
        : `Class ${classItem.classId}`;

    for (const teacher of classItem.teachers) {
      const teacherId = Number(teacher.teacherId);
      if (!Number.isFinite(teacherId)) continue;

      let row = loadByTeacherId.get(teacherId);
      if (!row) {
        row = {
          teacherId,
          name: teacherDisplayName(teacher),
          weeklySessions: 0,
          assignedClassCount: 0,
          classNames: [],
          isOverworked: false,
        };
        loadByTeacherId.set(teacherId, row);
      }

      row.weeklySessions += sessions;
      row.assignedClassCount += 1;
      row.classNames.push(className);
    }
  }

  return sortTeacherLoadRows(
    Array.from(loadByTeacherId.values()).map((row) => ({
      ...row,
      isOverworked: row.weeklySessions >= overworkedThreshold,
    })),
  );
}

export function teacherMatchesLoadBucket(
  weeklySessions: number,
  range: TeacherLoadBucketRange,
): boolean {
  if (range.max == null) {
    return weeklySessions >= range.min;
  }
  return weeklySessions >= range.min && weeklySessions <= range.max;
}

export function buildTeacherLoadBuckets(
  items: readonly TeacherLoadRow[],
  bucketRanges: TeacherLoadBucketRange[] = TEACHER_LOAD_BUCKET_RANGES,
): TeacherLoadBucket[] {
  return bucketRanges.map((range) => ({
    range,
    teachers: sortTeacherLoadRows(
      items.filter((row) => teacherMatchesLoadBucket(row.weeklySessions, range)),
    ),
  }));
}

export function getTeacherLoadBucketLabel(
  range: TeacherLoadBucketRange,
): string {
  return formatTeacherLoadBucketRangeLabel(range);
}

export function getTeacherLoadBucketTitle(range: TeacherLoadBucketRange): string {
  return formatTeacherLoadBucketRangeTitle(range);
}

export function getTeacherLoadBucketColor(bucketIndex: number): string {
  return (
    TEACHER_LOAD_BUCKET_COLORS[bucketIndex] ??
    TEACHER_LOAD_BUCKET_COLORS[TEACHER_LOAD_BUCKET_COLORS.length - 1]
  );
}
